import axios from "axios";
import { API_BASE_URL } from "./apiConfig";
import { clearAuth, getSession } from "./auth";

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true,
});

function normalizeToken(token) {
    return String(token || "").replace(/^Bearer\s+/i, "").trim();
}

function getBackendErrorCode(error) {
    const responseData = error.response?.data;
    return typeof responseData === "object"
        ? responseData.error || responseData.code
        : undefined;
}


export function getFriendlyErrorMessage(error, fallback = "Something went wrong. Please try again.") {
    if (!error.response && error.message && error.message !== "Network Error") {
        return error.message;
    }

    if (!error.response) {
        return "We could not connect to the server. Please check your connection and try again.";
    }

    const status = error.response.status;
    const responseData = error.response.data;
    const backendMessage =
        typeof responseData === "string" ? responseData : responseData?.message;
    const errorCode = getBackendErrorCode(error);
    const normalizedMessage = String(backendMessage || "").toLowerCase();

    const friendlyCodeMessages = {
        TOKEN_EXPIRED: "Your session has expired. Please log in again.",
        INVALID_CREDENTIALS: "The email or password is incorrect.",
        EMAIL_ALREADY_EXISTS: "An account with this email already exists.",
        USER_ALREADY_EXISTS: "This user already exists.",
        VALIDATION_FAILED: "Please check the entered details and try again.",
    };

    if (friendlyCodeMessages[errorCode]) return friendlyCodeMessages[errorCode];
    if (
        normalizedMessage.includes("user already exists") ||
        normalizedMessage.includes("email already exists")
    ) {
        return "A client with this email already exists.";
    }

    if (status === 400) return backendMessage || "Please check the entered details and try again.";
    if (status === 401) return "Your session has expired. Please log in again.";
    if (status === 403) return "You do not have permission to perform this action.";
    if (status === 404) return "The requested service could not be found.";
    if (status === 409) return backendMessage || "This record already exists.";
    if (status >= 500) return "The server is temporarily unavailable. Please try again later.";

    return backendMessage || fallback;
}

apiClient.interceptors.request.use((config) => {
    const session = getSession();
    const token = session?.token || normalizeToken(localStorage.getItem("token"));
    const isLoginRequest = config.url === "/auth/login";

    if (token && !isLoginRequest && !session) {
        clearAuth();
        return Promise.reject(new Error("Your session has expired. Please log in again."));
    }
    if (token && !isLoginRequest) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        const isLoginRequest = error.config?.url === "/auth/login";
        const isExpired =
            error.response?.status === 401 || getBackendErrorCode(error) === "TOKEN_EXPIRED";

        const headers = error.config?.headers;
        const requestToken = normalizeToken(headers?.get?.("Authorization") || headers?.Authorization || headers?.authorization);
        const currentToken = getSession()?.token || normalizeToken(localStorage.getItem("token"));
        // A response from before login must not invalidate the new session.
        if (isExpired && !isLoginRequest && requestToken && requestToken === currentToken) {
            clearAuth();
            // AuthProvider clears the UI session and returns to the public homepage.
        }

        return Promise.reject(error);
    },
);

export default apiClient;
